import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
} from '@mui/material';
import axios from 'axios';
import { toast } from 'react-toastify';

const AssignPatientDialog = ({ open, aidantId, onClose, onPatientAssigned, apiUrl, token }) => {
  const [availablePatients, setAvailablePatients] = useState([]);
  const [formData, setFormData] = useState({
    patient_profile_id: '',
    lien_patient_aidant: '',
    est_principal: 1,
    actif: 1,
    date_debut: new Date().toISOString().split('T')[0], // Date du jour par défaut (ex: 2026-09-09)
    date_fin: null,
    notes: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Charger la liste des patients pour le sélecteur
  useEffect(() => {
    if (open) {
      const fetchPatients = async () => {
        try {
          const response = await axios.get(`${apiUrl}/admin/patients`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const rawData = Array.isArray(response.data)
            ? response.data
            : response.data?.data || response.data?.patients || [];
          setAvailablePatients(rawData.filter(Boolean));
        } catch (err) {
          console.error('Erreur chargement patients', err);
        }
      };
      fetchPatients();
    }
  }, [open, apiUrl, token]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.patient_profile_id) {
      toast.warning('Veuillez sélectionner un patient.');
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${apiUrl}/admin/aidants/${aidantId}/patients`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Patient affecté avec succès !');
      onPatientAssigned();
      onClose();
    } catch (err) {
      console.error('Erreur affectation :', err);
      toast.error('Erreur lors de l\'affectation.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
      <form onSubmit={handleSubmit}>
        <DialogTitle sx={{ fontWeight: 800, color: '#2B3674' }}>
          Affecter un Patient à l'Aidant
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: '#E0E5F2' }}>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            {/* Sélection du patient */}
            <FormControl fullWidth size="small" required>
              <InputLabel>Patient</InputLabel>
              <Select
                name="patient_profile_id"
                value={formData.patient_profile_id}
                label="Patient"
                onChange={handleChange}
              >
                {availablePatients.map((pat) => (
  <MenuItem key={pat.id_patient_profile} value={pat.id_patient_profile}>
    {`[ID: ${pat.id_patient_profile}] ${pat.nom_patient} ${pat.prenom_patient} - Tél: ${pat.telephone_patient || 'Non renseigné'}`}
  </MenuItem>
))}
              </Select>
            </FormControl>

            {/* Lien de parenté */}
            <TextField
              label="Lien (ex: Frère, Père, Mère...)"
              name="lien_patient_aidant"
              value={formData.lien_patient_aidant}
              onChange={handleChange}
              required
              size="small"
              fullWidth
            />

            {/* Dates */}
            <Stack direction="row" spacing={2}>
              <TextField
                label="Date de début"
                name="date_debut"
                type="date"
                value={formData.date_debut}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                size="small"
                fullWidth
              />
              <TextField
                label="Date de fin (Optionnel)"
                name="date_fin"
                type="date"
                value={formData.date_fin || ''}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                size="small"
                fullWidth
              />
            </Stack>

            {/* Notes */}
            <TextField
              label="Notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              multiline
              rows={2}
              size="small"
              fullWidth
            />

            {/* Switches Actif & Principal */}
            <Stack direction="row" spacing={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.est_principal === 1}
                    onChange={(e) => setFormData({ ...formData, est_principal: e.target.checked ? 1 : 0 })}
                    color="primary"
                  />
                }
                label="Patient Principal"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.actif === 1}
                    onChange={(e) => setFormData({ ...formData, actif: e.target.checked ? 1 : 0 })}
                    color="primary"
                  />
                }
                label="Actif"
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={onClose} color="inherit" sx={{ fontWeight: 700, color: '#A3AED0' }}>
            Annuler
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            sx={{
              backgroundColor: '#4318FF',
              fontWeight: 700,
              borderRadius: '10px',
              textTransform: 'none',
              px: 3,
              '&:hover': { backgroundColor: '#3311CC' },
            }}
          >
            Affecter
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AssignPatientDialog;